import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { AmountInput, CurrenciesUSD, Icon, Input, Radio } from '@column/column-ui-kit';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  RadioGroup as TypeRadioGroup,
} from '@column/ui';

import { AchAccountsSequence } from '../_components/AchAccountsSequence';
import { RestrictedBadge } from '../_components/RestrictedBadge';
import { CreateTransfer } from '../Add';
import { DateInput, FormField, RadioGroup, RenderFields } from '~/components';
import { UnderlineLink } from '~/elements';
import { useSessionStore } from '~/stores/Session';
import { DefinitionList } from '~/styles';
import { SAME_DAY_ACH_CUTOFF_LABEL, getDateFormat, isPastSameDayAchCutoff } from '~/util';

const SameDayControl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  /* Align the short switch with the "Same Day" label, which carries a 12px top offset
     in the side-by-side layout. The guidance/warning message stacks left-aligned below it. */
  @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-top: 12px;
  }
`;

const SameDayHint = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const WarningIcon = styled(Icon.Warning)`
  --icon-color: ${({ theme }) => theme.warning.background};
  --icon-size: 14px;
  flex-shrink: 0;
  /* Optically center the icon against the first line of the message. */
  margin-top: 1px;
`;

// Animates the "Same Day" label description in/out (grid-rows 0fr↔1fr) when the switch is
// toggled. The text stays mounted so the exit animates smoothly; it inherits the muted
// description styling from FormField's <Description> wrapper.
const DescriptionReveal = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.2s ease;

  > div {
    overflow: hidden;
    min-height: 0;
  }
`;

// Faint helper chip shown inside the Effective Date input while the value is still the default
// (tomorrow); it disappears once a later date is picked.
const EffectiveDateTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  /* The vertical padding gives the chip its taller look; matching negative vertical margins
     cancel that added height so it never grows the input beyond its line box. */
  margin-top: -3px;
  margin-bottom: -3px;
  font-size: 11px;
  line-height: 1;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  user-select: none;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  /* Inset box-shadow renders as a stroke without adding to layout height like a border would. */
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(300)};
`;

// In the side-by-side layout the two halves break onto their own lines (a guaranteed wrap
// after "day"); on narrow screens they stay inline and wrap naturally, with text-wrap: pretty
// to avoid orphans. (balance is intentionally avoided — it would push "day" to the 2nd line.)
const Guidance = styled.span`
  text-wrap: pretty;

  @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    > span {
      display: block;
    }
  }
`;

export const EntryClassCodeDefinitions = (
  <>
    <DefinitionList>
      <dd>
        A Standard Entry Class (SEC) code is a three-character code used to identify various properties of ACH
        transfers. Column supports all SEC codes for incoming ACH transfers. However, for outgoing ACH transfers that
        are initiated via the dashboard, only the following codes are supported:
      </dd>
      <dt>CCD</dt>
      <dd>
        Corporate Credit or Debit: Entry initiated by an Organization to transfer funds to or from an account of that
        Organization or another Organization.
      </dd>
      <dt>CIE</dt>
      <dd>
        Customer Initiated Entry: Credit Entry initiated by or on behalf of the holder of a Consumer Account to transfer
        funds to the account of the Receiver.
      </dd>
      <dt>CTX</dt>
      <dd>
        Corporate Trade Exchange: Entry initiated by an Organization to transfer funds to or from the account of that
        Organization or another Organization that permits the inclusion of payment-related remittance information in
        ANSI or UN/EDIFACT syntax.
      </dd>
      <dt>PPD</dt>
      <dd>
        Prearranged Payment and Deposit: Entry initiated by an Organization based on a standing or a Single Entry
        authorization from a Receiver to transfer funds to or from a Consumer Account of the Receiver.
      </dd>
      <dt>TEL</dt>
      <dd>
        Telephone-Initiated Entry: Debit initiated by an Originator pursuant to an oral authorization obtained over the
        telephone to transfer funds from a Consumer Account of the Receiver.
      </dd>
      <dt>WEB</dt>
      <dd>
        Internet-Initiated/Mobile Entry: (1) a debit Entry initiated by an Originator to a Consumer Account of the
        Receiver based on (a) an authorization that is communicated, other than by an oral communication, from the
        Receiver to the Originator via the Internet or a Wireless Network, or (b) any form of authorization if the
        Receiver's instruction for the initiation of the individual debit Entry is designed by the Originator to be
        communicated, other than by an oral communication, to the Originator via a Wireless Network; or (2) a credit
        Entry initiated by or on behalf of the holder of a Consumer Account that is intended for the Consumer Account of
        a Receiver, regardless of whether the authorization of such Entry is communicated via the Internet or Wireless
        Network.
      </dd>
    </DefinitionList>

    <UnderlineLink href="https://docs.column.com/ach/types#ach-standard-entry-class-codes" newTab>
      Learn more about Entry Class Codes
    </UnderlineLink>
  </>
);

export const IdempotencyKeyDefinitions = (
  <>
    <DefinitionList>
      <dd>
        Column automatically generates idempotency keys for safely retrying transfers without accidentally duplicating
        the result. Custom idempotency keys can be set by clearing the default value and replacing it with your own key.
        This can be helpful when your API call fails and you aren't sure if Column received the request. Idempotency
        enables you to retry a request without worrying it will create a duplicate transaction.
      </dd>
      <dd>
        In order to perform an idempotent request, provide any unique string up to 255 characters long and is case
        sensitive. Column reserves the right to expire idempotency keys after 30 days.
      </dd>
    </DefinitionList>
    <UnderlineLink href="https://docs.column.com/working-with-the-api/idempotency" newTab>
      Learn more about Idempotency Keys
    </UnderlineLink>
  </>
);

const AchContent: FC<ComponentLoaderTypeProps> = ({ onLoaded }) => {
  const [isSameDay, setIsSameDay] = useState<boolean>(false);
  // Tomorrow at LOCAL midnight — a minDate carrying a time-of-day would wrongly disable the tomorrow cell.
  const tomorrowDate = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date;
  }, []);
  const tomorrow = useMemo(() => getDateFormat(tomorrowDate), [tomorrowDate]);
  // Re-check the cutoff on an interval so the warning still appears if the form stays open across it.
  const [cutoffPassed, setCutoffPassed] = useState<boolean>(() => isPastSameDayAchCutoff());
  useEffect(() => {
    const interval = setInterval(() => setCutoffPassed(isPastSameDayAchCutoff()), 30_000);
    return () => clearInterval(interval);
  }, []);
  const showSameDayWarning = isSameDay && cutoffPassed;
  const { isAuthorized } = useSessionStore();

  useEffect(() => {
    onLoaded();
  }, []);

  const isDebitAuthorized = useMemo(
    () =>
      isAuthorized({
        permission: 'achDebits',
        permissionLevel: 'write',
      }),
    [isAuthorized]
  );
  const isCreditAuthorized = useMemo(
    () =>
      isAuthorized({
        permission: 'achCredits',
        permissionLevel: 'write',
      }),
    [isAuthorized]
  );

  return (
    <>
      {RenderFields<CreateTransfer, 'ach'>({
        prefix: 'ach',
        sections: [
          {
            headline: 'Information',
            fields: [
              {
                id: 'amount',
                label: 'Amount',
                rules: { required: true, validate: (v) => Number(v) > 0 },
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <AmountInput
                    hasError={isTouched && !!error}
                    value={Number(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                    currencyList={CurrenciesUSD}
                    fixedCurrencyCode="USD"
                  />
                ),
              },
              {
                id: 'type',
                label: 'Type',
                description: 'Select between ACH Debit or ACH Credit.',
                rules: { required: true },
                defaultValue: isCreditAuthorized ? 'CREDIT' : 'DEBIT',
                children: ({ value, onChange }) => (
                  // Side-by-side on desktop, stacked on mobile (orientation="responsive").
                  // Radix prevents selecting a disabled item, so unauthorized types can't be
                  // chosen even though the whole row is otherwise clickable.
                  <TypeRadioGroup value={String(value ?? '')} onValueChange={onChange}>
                    <FieldGroup variant="outline" orientation="responsive">
                      <Field
                        orientation="horizontal"
                        className="relative"
                        data-disabled={!isCreditAuthorized || undefined}
                      >
                        <RadioGroupItem value="CREDIT" id="ach-type-credit" disabled={!isCreditAuthorized} />
                        <FieldContent>
                          <FieldLabel htmlFor="ach-type-credit">Credit</FieldLabel>
                          <FieldDescription className="text-xs text-pretty">
                            Push money from your bank account to a counterparty.
                          </FieldDescription>
                        </FieldContent>
                        {!isCreditAuthorized && (
                          <RestrictedBadge content={'You do not have permission to use ACH Credits.'} />
                        )}
                      </Field>
                      <Field
                        orientation="horizontal"
                        className="relative"
                        data-disabled={!isDebitAuthorized || undefined}
                      >
                        <RadioGroupItem value="DEBIT" id="ach-type-debit" disabled={!isDebitAuthorized} />
                        <FieldContent>
                          <FieldLabel htmlFor="ach-type-debit">Debit</FieldLabel>
                          <FieldDescription className="text-xs text-pretty">
                            Pull money from a counterparty to your bank account.
                          </FieldDescription>
                        </FieldContent>
                        {!isDebitAuthorized && (
                          <RestrictedBadge content={'You do not have permission to use ACH Debits.'} />
                        )}
                      </Field>
                    </FieldGroup>
                  </TypeRadioGroup>
                ),
              },
              {
                content: <AchAccountsSequence />,
              },
              {
                // Same Day and Effective Date share a single grid item. Below the switch, a guidance/warning
                // message flips in place (its own accordion); separately, the Effective Date field collapses
                // when Same Day is on. Both transitions are accordion-animated, so nothing mounts instantly
                // and the surrounding page layout stays put.
                content: (
                  <>
                    <FormField<CreateTransfer>
                      id="ach.sameDay"
                      label="Same Day"
                      defaultValue={false}
                      tooltip="Enable to send this as a same-day ACH transfer. When enabled, the transfer settles the same business day and takes precedence over the effective date."
                      // Guidance sits under the "Same Day" label and animates out when the switch is on.
                      description={
                        <DescriptionReveal $open={!showSameDayWarning}>
                          <div>
                            <Guidance>
                              <span>Must be sent on a business day</span>{' '}
                              <span>before {SAME_DAY_ACH_CUTOFF_LABEL}</span>
                            </Guidance>
                          </div>
                        </DescriptionReveal>
                      }
                    >
                      {({ value, onChange }) => (
                        <SameDayControl>
                          <Switch
                            checked={Boolean(value)}
                            onCheckedChange={(checked) => {
                              onChange(checked);
                              setIsSameDay(checked);
                            }}
                          />
                          {/* The past-cutoff warning animates in below the switch when Same Day is on. */}
                          <Accordion type="single" value={showSameDayWarning ? 'warning' : ''}>
                            <AccordionItem value="warning" className="border-b-0">
                              <AccordionContent className="pt-2 pb-0">
                                <SameDayHint>
                                  <WarningIcon />
                                  The deadline to guarantee same-day settlement has passed. This transfer will settle
                                  the following business day by 5:30 AM PT.
                                </SameDayHint>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </SameDayControl>
                      )}
                    </FormField>
                    {/* Effective Date shows only when Same Day is off; it collapses (animated) when on. */}
                    <Accordion type="single" value={isSameDay ? '' : 'effectiveDate'}>
                      <AccordionItem value="effectiveDate" className="border-b-0">
                        <AccordionContent className="pt-6 pb-0">
                          <FormField<CreateTransfer>
                            id="ach.effectiveDate"
                            label="Effective Date"
                            defaultValue={tomorrow}
                            tooltip="Defaults to tomorrow. Pick a later date to schedule the transfer, or enable Same Day to send it today."
                          >
                            {({ value, onChange }, { isTouched, error }) => {
                              const currentValue = value ? String(value) : tomorrow;
                              return (
                                <DateInput
                                  disableBefore={tomorrowDate}
                                  value={currentValue}
                                  onChange={(next) => onChange(next ?? tomorrow)}
                                  hasError={isTouched && !!error}
                                  trailingAdornment={
                                    currentValue === tomorrow ? (
                                      <EffectiveDateTag>Tomorrow</EffectiveDateTag>
                                    ) : undefined
                                  }
                                />
                              );
                            }}
                          </FormField>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </>
                ),
              },
              {
                id: 'entryClassCode',
                label: 'Entry Class Code',
                defaultValue: 'CCD',
                tooltip: {
                  headline: 'Entry Class Code Definitions',
                  content: EntryClassCodeDefinitions,
                },
                rules: {
                  required: true,
                },
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Select
                    value={String(value ?? '')}
                    onValueChange={onChange}
                    onOpenChange={(open) => !open && onBlur()}
                  >
                    <SelectTrigger className="w-full" aria-invalid={!!error}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['CCD', 'CIE', 'CTX', 'PPD', 'TEL', 'WEB'].map((code) => (
                        <SelectItem key={code} value={code} data-cy={`dropdown-option-${code}`}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ),
              },
              {
                id: 'description',
                label: 'Description',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 255,
                    message: 'Description cannot exceed 255 characters.',
                  },
                },
                tooltip:
                  'Optional field to add a description of the transfer for your internal reference and is not sent externally. Maximum 255 characters.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Description"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
            ],
          },
          {
            headline: 'Company',
            fields: [
              {
                id: 'companyName',
                label: 'Name',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 16,
                    message: 'Company name cannot exceed 16 characters.',
                  },
                },
                tooltip:
                  'Optional field to add the name of the company which initiated the ACH transfer. Maximum 16 characters.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Company Name"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'companyEntryDescription',
                label: 'Entry Description',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 10,
                    message: 'Entry description cannot exceed 10 characters.',
                  },
                },
                tooltip:
                  'Optional field to provide the Receiver with a description of the purpose of the transfer. Default value: "PAYMENT". Maximum 10 characters.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Company Entry Description"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'companyDiscretionaryData',
                label: 'Discretionary Data',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 20,
                    message: 'Discretionary data cannot exceed 20 characters.',
                  },
                },
                tooltip:
                  'Optional field to include codes (one or more), of significance only to you, to enable specialized handling of the transfer. There is no standardized interpretation for the value of the field. Maximum 20 characters.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Company Discretionary Data"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
            ],
          },
          {
            headline: 'Receiver',
            fields: [
              {
                id: 'receiverName',
                label: 'Name',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 22,
                    message: 'Receiver name cannot exceed 22 characters.',
                  },
                },
                tooltip:
                  'Optional field to add the name of the receiver, usually the name on the bank account. Maximum 22 characters.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Receiver Name"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'receiverId',
                label: 'ID',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 15,
                    message: 'Receiver ID cannot exceed 15 characters.',
                  },
                },
                tooltip:
                  'Optional field to add an accounting reference number by which the Receiver is known to the Originator. It is included for further identification and for descriptive purposes. For example, it may be the number shown on an invoice, statement, billhead, notice, or other communication as the reference for the Receiver to update account receivable records. Maximum 15 characters.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Receiver ID"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
            ],
          },
          {
            headline: 'Advanced',
            isCollapsed: true,
            fields: [
              {
                id: 'allowOverdraft',
                label: 'Allow Overdraft',
                defaultValue: false,
                tooltip:
                  'Selecting "Yes" allows an outgoing transfer to cause the selected account to go negative. (Only eligible on overdraftable accounts.)',
                children: ({ value, onChange }) => (
                  <RadioGroup>
                    <Radio isChecked={!Boolean(value)} onCheckedChange={() => onChange(false)} label="No" />
                    <Radio isChecked={Boolean(value)} onCheckedChange={onChange} label="Yes" />
                  </RadioGroup>
                ),
              },
              {
                id: 'paymentRelatedInfo',
                label: 'Payment Related Info',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 80,
                    message: 'Payment related info cannot exceed 80 characters.',
                  },
                },
                tooltip:
                  'Optional field to add payment instructions to the receiving depository financial institution (RDFI). Note, the visibility of this information to the end recipient can vary.',
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Payment Related Info"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'idempotencyKey',
                label: 'Idempotency Key',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 255,
                    message: 'Idempotency key cannot exceed 255 characters.',
                  },
                },
                children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    placeholder="Idempotency Key"
                    hasError={isTouched && !!error}
                    value={String(value)}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
                tooltip: {
                  headline: 'Idempotency Keys',
                  content: IdempotencyKeyDefinitions,
                },
              },
            ],
          },
        ],
      })}
    </>
  );
};

export default AchContent;
